/**
 * Ticket mutation hooks — status / priority / assignee.
 *
 * Each mutation:
 *   1. Optimistically updates the detail cache (single Ticket).
 *   2. Optimistically updates the list cache (Triage Inbox row).
 *   3. Reconciles against the server response on success.
 *   4. Rolls back BOTH caches on error and surfaces a sonner toast
 *      (INTERACTION_SPEC §3 + §7).
 *
 * Same shape as `useSendMessage`, just simpler — single field update,
 * no thread insert.
 */

import {
  useMutation,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';

import { ticketsApi } from '@beacon/api-client';
import type {
  Ticket,
  TicketPriority,
  TicketStatus,
  Uuid,
} from '@beacon/core';

import { beaconClient } from '@/lib/api/client.js';
import { ticketKeys } from './use-tickets.js';

interface UpdateContext {
  previousDetail: Ticket | undefined;
  previousList: Ticket[] | undefined;
  number: string;
}

function applyToBothCaches(
  qc: QueryClient,
  number: string,
  patch: (t: Ticket) => Ticket
): UpdateContext {
  const detailKey = ticketKeys.detail(number);
  const listKey = ticketKeys.list();

  const previousDetail = qc.getQueryData<Ticket>(detailKey);
  const previousList = qc.getQueryData<Ticket[]>(listKey);

  if (previousDetail) {
    qc.setQueryData<Ticket>(detailKey, patch(previousDetail));
  }
  if (previousList) {
    qc.setQueryData<Ticket[]>(
      listKey,
      previousList.map((t) => (t.number === number ? patch(t) : t))
    );
  }

  return { previousDetail, previousList, number };
}

function rollback(qc: QueryClient, ctx: UpdateContext) {
  if (ctx.previousDetail !== undefined) {
    qc.setQueryData(ticketKeys.detail(ctx.number), ctx.previousDetail);
  }
  if (ctx.previousList !== undefined) {
    qc.setQueryData(ticketKeys.list(), ctx.previousList);
  }
}

function reconcileOnSuccess(
  qc: QueryClient,
  number: string,
  server: Ticket
) {
  qc.setQueryData(ticketKeys.detail(number), server);
  qc.setQueryData<Ticket[]>(ticketKeys.list(), (current) =>
    current?.map((t) => (t.number === number ? server : t)) ?? current
  );
}

// ----------------------------------------------------------------
// Status
// ----------------------------------------------------------------
export function useUpdateTicketStatus() {
  const qc = useQueryClient();

  return useMutation<
    Ticket,
    Error,
    { number: string; status: TicketStatus; silent?: boolean },
    UpdateContext
  >({
    mutationFn: ({ number, status }) =>
      ticketsApi.updateStatus(beaconClient, number, status),

    onMutate: async ({ number, status }) => {
      await qc.cancelQueries({ queryKey: ticketKeys.detail(number) });
      await qc.cancelQueries({ queryKey: ticketKeys.list() });
      return applyToBothCaches(qc, number, (t) => ({
        ...t,
        status,
        ...(status === 'resolved' && {
          resolvedAt: new Date().toISOString() as Ticket['resolvedAt'],
        }),
        ...(status === 'closed' && {
          closedAt: new Date().toISOString() as Ticket['closedAt'],
        }),
      }));
    },

    onSuccess: (server, { number }) =>
      reconcileOnSuccess(qc, number, server),

    // `silent` suppresses the per-ticket toast for bulk callers, who
    // surface a single aggregated toast covering the whole batch. The
    // optimistic rollback still fires regardless.
    onError: (err, vars, ctx) => {
      if (ctx) rollback(qc, ctx);
      if (!vars.silent) {
        toast.error("Couldn't change status", {
          description: err.message,
        });
      }
    },
  });
}

// ----------------------------------------------------------------
// Priority
// ----------------------------------------------------------------
export function useUpdateTicketPriority() {
  const qc = useQueryClient();

  return useMutation<
    Ticket,
    Error,
    { number: string; priority: TicketPriority; silent?: boolean },
    UpdateContext
  >({
    mutationFn: ({ number, priority }) =>
      ticketsApi.updatePriority(beaconClient, number, priority),

    onMutate: async ({ number, priority }) => {
      await qc.cancelQueries({ queryKey: ticketKeys.detail(number) });
      await qc.cancelQueries({ queryKey: ticketKeys.list() });
      return applyToBothCaches(qc, number, (t) => ({ ...t, priority }));
    },

    onSuccess: (server, { number }) =>
      reconcileOnSuccess(qc, number, server),

    onError: (err, vars, ctx) => {
      if (ctx) rollback(qc, ctx);
      if (!vars.silent) {
        toast.error("Couldn't change priority", {
          description: err.message,
        });
      }
    },
  });
}

// ----------------------------------------------------------------
// Assignee
// ----------------------------------------------------------------
export function useAssignTicket() {
  const qc = useQueryClient();

  return useMutation<
    Ticket,
    Error,
    { number: string; assigneeUserId: Uuid | null; silent?: boolean },
    UpdateContext
  >({
    mutationFn: ({ number, assigneeUserId }) =>
      ticketsApi.assign(beaconClient, number, assigneeUserId),

    onMutate: async ({ number, assigneeUserId }) => {
      await qc.cancelQueries({ queryKey: ticketKeys.detail(number) });
      await qc.cancelQueries({ queryKey: ticketKeys.list() });
      return applyToBothCaches(qc, number, (t) => ({
        ...t,
        assigneeUserId,
      }));
    },

    onSuccess: (server, { number }) =>
      reconcileOnSuccess(qc, number, server),

    onError: (err, vars, ctx) => {
      if (ctx) rollback(qc, ctx);
      if (!vars.silent) {
        toast.error("Couldn't reassign ticket", {
          description: err.message,
        });
      }
    },
  });
}
